<script lang="ts" module>
  export type Editor = 'glide' | 'start' | 'wind' | null;
</script>

<script lang="ts">
  // The three inputs every flight needs, as chips that open inline editors:
  // glide ratio, start altitude and wind.
  import { settings } from '../lib/state.svelte';
  import { sources } from '../sources';
  import { nearestLevel } from '../sources/openmeteo';
  import type { WindForecast } from '../sources/types';
  import type { LngLat } from '../lib/geo';
  import {
    altStep,
    altToM,
    compass,
    fmtAlt,
    fmtSpeed,
    kmhToSpeed,
    mToAlt,
    speedStep,
    speedToKmh,
    SPEED_LABEL,
  } from '../lib/units';
  import Icon from './Icon.svelte';
  import Stepper from './Stepper.svelte';
  import WindDial from './WindDial.svelte';

  interface Props {
    open: Editor;
    forecastAt: () => LngLat | null;
    /** Altitude used to pick the forecast level. */
    flyingAlt: () => number;
    /** Glide ratio override shown instead of the setting (region-search hand-off). */
    grOverride?: number | null;
    onclearoverride?: () => void;
  }
  let { open = $bindable(), forecastAt, flyingAlt, grOverride = null, onclearoverride }: Props = $props();

  const u = $derived(settings.units);
  const calm = $derived(settings.windSpeed <= 0);
  const toggle = (e: Exclude<Editor, null>) => (open = open === e ? null : e);

  // Approximate calm-air trim glide by certification class; real-world glide is lower.
  const PRESETS: [string, number][] = [
    ['Cautious', 6],
    ['EN-A', 8],
    ['EN-B', 9],
    ['EN-C', 10],
    ['EN-D', 11],
  ];

  // speed ranges in display units
  const maxSpeed = $derived(Math.round(kmhToSpeed(60, u)));
  const quickSpeeds = $derived(
    u.speed === 'kt' ? [5, 10, 15] : u.speed === 'ms' ? [3, 5, 8] : u.speed === 'mph' ? [5, 10, 15] : [10, 20, 30],
  );

  // ---- forecast ----
  let forecast = $state<WindForecast | null>(null);
  let hour = $state(0);
  let fcError = $state('');
  let fcLoading = $state(false);

  async function loadForecast() {
    const at = forecastAt();
    if (!at) {
      fcError = 'Set a takeoff first.';
      return;
    }
    fcError = '';
    fcLoading = true;
    try {
      forecast = await sources.wind.forecast(at);
      const now = new Date();
      hour = Math.max(0, forecast.hours.findIndex((h) => new Date(h.time) >= now));
      applyHour();
    } catch (e) {
      fcError = e instanceof Error ? e.message : String(e);
    } finally {
      fcLoading = false;
    }
  }

  function applyHour() {
    const h = forecast?.hours[hour];
    const l = h && nearestLevel(h.levels, flyingAlt());
    if (!l) return;
    settings.windSpeed = Math.round(l.speed);
    settings.windFrom = Math.round(l.from / 5) * 5 % 360;
  }

  const level = $derived(forecast?.hours[hour] ? nearestLevel(forecast.hours[hour].levels, flyingAlt()) : undefined);
  const hourLabel = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
</script>

<div class="chips" role="toolbar" aria-label="Flight setup">
  <button class="chip" class:open={open === 'glide'} aria-expanded={open === 'glide'} onclick={() => toggle('glide')}>
    <Icon name="glide" size={18} />
    <span><small>Glide</small>{(grOverride ?? settings.gr).toFixed(1)}:1</span>
  </button>
  <button class="chip" class:open={open === 'start'} aria-expanded={open === 'start'} onclick={() => toggle('start')}>
    <Icon name="mountain" size={18} />
    <span><small>Start</small>{settings.startMode === 'ground' ? 'Launch' : fmtAlt(settings.startAlt, u)}</span>
  </button>
  <button class="chip" class:open={open === 'wind'} aria-expanded={open === 'wind'} onclick={() => toggle('wind')}>
    {#if calm}
      <Icon name="wind" size={18} />
    {:else}
      <svg width="18" height="18" viewBox="-10 -10 20 20" aria-hidden="true" style="transform: rotate({settings.windFrom}deg)">
        <path d="M0 -8 V6 M-4 2 L0 7 L4 2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    {/if}
    <span><small>Wind</small>{calm ? 'Calm' : `${fmtSpeed(settings.windSpeed, u)} ${compass(settings.windFrom)}`}</span>
  </button>
</div>

{#if open === 'glide'}
  <div class="editor">
    {#if grOverride !== null}
      <p class="hint">
        This route uses {grOverride}:1 from the region search.
        <button class="link" onclick={onclearoverride}>Use my glide ratio</button>
      </p>
    {/if}
    <div class="row">
      <Stepper label="Glide ratio" value={settings.gr} step={0.5} min={2} max={11} decimals={1} suffix=":1" onchange={(v) => (settings.gr = v)} />
      <input class="grow" type="range" min="2" max="11" step="0.5" bind:value={settings.gr} aria-label="Glide ratio slider" />
    </div>
    <div class="presets" role="group" aria-label="Glide presets">
      {#each PRESETS as [name, gr] (name)}
        <button class:on={settings.gr === gr} onclick={() => (settings.gr = gr)}>{name}<small>{gr}</small></button>
      {/each}
    </div>
    <p class="hint">
      Distance per unit of height lost through the air. Class values are typical calm-air trim glides; sink,
      turbulence and speed-bar lower them, so plan below your glider's figure.
    </p>
  </div>
{:else if open === 'start'}
  <div class="editor">
    <div class="seg wide" role="radiogroup" aria-label="Start altitude">
      <button role="radio" aria-checked={settings.startMode === 'ground'} class:on={settings.startMode === 'ground'} onclick={() => (settings.startMode = 'ground')}>From launch</button>
      <button role="radio" aria-checked={settings.startMode === 'fixed'} class:on={settings.startMode === 'fixed'} onclick={() => (settings.startMode = 'fixed')}>At altitude</button>
    </div>
    {#if settings.startMode === 'fixed'}
      <div class="row">
        <Stepper
          label="Start altitude"
          value={Math.round(mToAlt(settings.startAlt, u))}
          step={altStep(u) * 2}
          min={0}
          max={Math.round(mToAlt(4500, u))}
          suffix=" {u.alt}"
          onchange={(v) => (settings.startAlt = altToM(v, u))}
        />
        <span class="hint">above sea level</span>
      </div>
      <p class="hint">For example the top of a thermal. If it is below the takeoff, the takeoff height is used.</p>
    {:else}
      <p class="hint">You launch from the ground at the takeoff.</p>
    {/if}
  </div>
{:else if open === 'wind'}
  <div class="editor">
    <div class="wind">
      <WindDial from={settings.windFrom} {calm} onchange={(d) => (settings.windFrom = d)} />
      <div class="wind-side">
        <p class="wind-now">
          {#if calm}<b>Calm</b> <span class="hint">still air</span>
          {:else}<b>{fmtSpeed(settings.windSpeed, u)}</b> from {compass(settings.windFrom)} <span class="hint">{settings.windFrom}°</span>{/if}
        </p>
        <Stepper
          label="Wind speed"
          value={+kmhToSpeed(settings.windSpeed, u).toFixed(1)}
          step={speedStep(u)}
          min={0}
          max={maxSpeed}
          decimals={u.speed === 'ms' ? 1 : 0}
          suffix=" {SPEED_LABEL[u.speed]}"
          onchange={(v) => (settings.windSpeed = speedToKmh(v, u))}
        />
        <div class="presets small" role="group" aria-label="Quick wind speeds">
          <button class:on={calm} onclick={() => (settings.windSpeed = 0)}>Calm</button>
          {#each quickSpeeds as s (s)}
            <button onclick={() => (settings.windSpeed = speedToKmh(s, u))}>{s}</button>
          {/each}
        </div>
      </div>
    </div>
    <p class="hint">Drag around the compass to set where the wind comes from.</p>
    <div class="row">
      <span class="label">Glider trim speed</span>
      <Stepper
        label="Trim airspeed"
        value={Math.round(kmhToSpeed(settings.airspeed, u))}
        step={1}
        min={Math.round(kmhToSpeed(25, u))}
        max={Math.round(kmhToSpeed(60, u))}
        suffix=" {SPEED_LABEL[u.speed]}"
        onchange={(v) => (settings.airspeed = speedToKmh(v, u))}
      />
    </div>
    <div class="row">
      <button class="btn" onclick={loadForecast} disabled={fcLoading}>{fcLoading ? 'Loading…' : forecast ? 'Reload forecast' : 'Use forecast'}</button>
      {#if forecast}
        <select bind:value={hour} onchange={applyHour} aria-label="Forecast hour">
          {#each forecast.hours as h, i (h.time)}
            <option value={i}>{hourLabel(h.time)}</option>
          {/each}
        </select>
      {/if}
    </div>
    {#if level}
      <p class="hint">
        Forecast at {level.level}{level.altitude ? ` (≈ ${fmtAlt(level.altitude, u)})` : ''}: {fmtSpeed(level.speed, u)} from
        {compass(level.from)}. Adjust above if you know better.
      </p>
    {/if}
    {#if fcError}<p class="err">{fcError}</p>{/if}
  </div>
{/if}

<style>
  .chips {
    display: flex;
    gap: 6px;
  }
  .chip {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--line);
    background: #fff;
    border-radius: 12px;
    padding: 6px 8px;
    text-align: left;
    color: var(--fg);
  }
  .chip span {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    font-weight: 650;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .chip small {
    font-size: 11px;
    font-weight: 500;
    color: var(--muted);
  }
  .chip.open {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .chip :global(svg) {
    flex: none;
    color: #475569;
  }
  .editor {
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #fbfcfd;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .grow {
    flex: 1;
    min-width: 100px;
  }
  .label {
    flex: 1;
    font-size: 14px;
  }
  .presets {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .presets button {
    border: 1px solid var(--line);
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    gap: 5px;
    align-items: baseline;
  }
  .presets button small {
    color: var(--muted);
    font-weight: 500;
  }
  .presets button.on {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }
  .presets button.on small {
    color: #dbeafe;
  }
  .presets.small button {
    padding: 5px 10px;
  }
  .seg.wide button {
    flex: 1;
  }
  .wind {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .wind-side {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .wind-now {
    margin: 0;
    font-size: 15px;
  }
  .hint {
    margin: 0;
  }
</style>
